// @ts-strict-ignore
import {
  AfterViewInit,
  ChangeDetectionStrategy,
  Component,
  ElementRef, EventEmitter,
  HostBinding,
  Input,
  OnDestroy,
  OnInit,
  Output,
  ViewChild
} from '@angular/core';
import { LMModel } from '@form/model';
import { LMFieldBaseComponent } from '@field/lm-field-base.component';
import { LMFieldServiceAdapter } from '@field/lm-field-service-adapter';
import { SafeUrl } from '@angular/platform-browser';
import { LMFileUploadService } from '@services/file-upload/lm-fileupload.service';
import * as ExifReader from 'exifreader';
import { BehaviorSubject, fromEvent } from 'rxjs';
import { AppConstants } from '@app/utils';
import { LMTextEvent } from '@field/text';
import { LMLargeImageIsDialogService } from '@modal/large-image-is/lm-large-image-is-dialog.service';
import * as urltemplate from 'url-template';

export class ImageMenuEntry {
  menuId: string;
  disabled?: boolean;
  text: string;
}

enum LMImageTypeDisplayEnum {
  READ_ONLY = 0,
  PLAIN
}

@Component({
  selector:    'lm-image',
  standalone: false,
  templateUrl: './lm-image.component.html',
  styleUrls:   [ './lm-image.component.scss' ],
  changeDetection: ChangeDetectionStrategy.OnPush
})
export class LMImageComponent extends LMFieldBaseComponent implements OnInit, AfterViewInit, OnDestroy {

  @HostBinding('attr.data-automation-id') dataFieldAutomationId = '';

  @Input() layoutObject: LMImageLayout;
  @Input() specFields: LMSpecFields;
  @Input() model: LMModel;

  @Output() fieldEvent: EventEmitter<LMFieldEvent> = new EventEmitter<LMFieldEvent>();

  @ViewChild('fileInput') fileInput: ElementRef;
  @ViewChild('fileImage') fileImage: ElementRef;
  @ViewChild('imgCanvas') imgCanvas: ElementRef;

  @HostBinding('class') imageClass = '';

  public menuId: string;
  public contextEntries: Array<ImageMenuEntry> = [];
  public imageHeight: number;
  public imageWidth: number;
  public imageUrl: BehaviorSubject<string | SafeUrl>;//string | SafeUrl;
  public hasUpdateAction: boolean;
  public missingImageUrl: string | SafeUrl;
  public missingImage: BehaviorSubject<boolean>;
  public isRandomBackground: boolean;
  public isForeGroundText: boolean;
  public foreGroundText: string;
  public bgColorClass: string;
  public imageAltText: string;
  public isSmartPhone: boolean;

  public displayType: LMImageTypeDisplayEnum;
  public layoutDisplayType;
  public largeImageIs: LMLargeImageIs= null;
  public largeImageIsInfo: LMLargeImageIsInfo = {};

  // Field Base has showLabel which is used for compound fields
  // So calling this displayLabel to avoid conflict between the two
  public displayLabel = false;

  // TODO: //maf look into using $theme-size-font-base?
  private lines: string = undefined;
  private perspective: string = undefined;
  //JFieldBinarySelectionField > IMAGE_LINES_DEFAULT
  private IMAGE_LINES_DEFAULT = 8;

  private getImageClass(): string {
    let imageClass = 'portrait-image';

    switch (this.layoutObject && this.layoutObject.displayImageAs) {
      case 'photo':
        imageClass = 'photo-image';
        break;

      case 'full':
        imageClass = 'full-image';
        break;

      // falls through to break
      default:
      case 'portrait':
        imageClass = 'portrait-image';
        break;
    }

    return imageClass;
  }

  constructor(
    protected lmFieldServiceAdapter: LMFieldServiceAdapter,
    private lmFileUploadService: LMFileUploadService,
    private lmLargeImageIsDialogService: LMLargeImageIsDialogService
  ) {
    super(lmFieldServiceAdapter);
    this.layoutDisplayType = LMImageTypeDisplayEnum;
  }

  ngOnInit() {
    super.ngOnInit();
    this.initialize(this.layoutObject, this.specFields, this.model);
    this.menuId = this.field.bindId + '_imageMenu';
    this.isSmartPhone = this?.lmFieldServiceAdapter?.lmContextService?.isSmartPhone();

    if (this.layoutObject.hasOwnProperty('displayImageAs')) {
      this.perspective = this.layoutObject.displayImageAs;
    }

    if (this.layoutObject.hasOwnProperty('rowspan')) {
      this.lines = this.layoutObject.rowspan;
    }

    if (this.layoutObject.hasOwnProperty('largeImage')) {
      this.largeImageIs = this.layoutObject.largeImage;
      this.buildLargeImageInfo();
    }

    if (this.perspective === 'full') {
      // noop
    } else if (this.lines) {
      this.imageHeight = this.imageWidth = parseInt(this.lines, 10) * AppConstants.DEFAULT_LINE_HEIGHT;
    } else {
      this.imageHeight = this.imageWidth = this.IMAGE_LINES_DEFAULT *   AppConstants.DEFAULT_LINE_HEIGHT;
    }

    const value = this.modelField.blobUrl !== undefined ? this.modelField.blobUrl : this.modelField.value;
    const url = this.lmFieldServiceAdapter.lmFormatUtilService.toImageUrlValue(value, this.lines, this.perspective);
    this.imageUrl = new BehaviorSubject<string | SafeUrl>(url);
    this.missingImage = new BehaviorSubject<boolean>(this.modelField.missingImage);
    this.isForeGroundText = this.field.foregroundText;
    this.isRandomBackground = this.field.isRandomBackground;

    if (this.missingImage.getValue()) {
      this.missingImageUrl = url;
    } else if (this.layoutObject.url) {
      this.missingImageUrl = this.layoutObject.url;
    }

    if (this.isForeGroundText) {
      const msgRule: LMMessageRule = this.field.foregroundText;
      const text: string = this.lmFieldServiceAdapter.lmFormatUtilService.formatMessageRule(msgRule, this.model, this.specFields);
      this.foreGroundText = this.lmFieldServiceAdapter.lmThemeService.getImageForegroundText(text);
    }

    if (this.isRandomBackground) {
      this.bgColorClass = this.getBgColorClass();
    }

    if (this.isReadOnly) {
      this.displayType = this.layoutDisplayType.READ_ONLY;
      this.imageAltText = this.foreGroundText;
    } else {
      this.displayType = this.layoutDisplayType.PLAIN;
      this.imageAltText = this.lmFieldServiceAdapter.lmFormatUtilService.formatMessage(Soho.Locale.translate('ImageEditAltText'), this.foreGroundText || '');
    }

    this.imageClass = this.getImageClass();
    this.hasUpdateAction = this.allowUpdate();
    this.checkLabelVisibility();

    // Field's model change happens during paging operations
    this.subscriptionArray.push(this.model.modelChanged
      .subscribe(() => {
        const value = this.modelField.blobUrl !== undefined ? this.modelField.blobUrl : this.modelField.value;
        this.imageUrl.next(this.lmFieldServiceAdapter.lmFormatUtilService.toImageUrlValue(value, this.lines, this.perspective));
        this.missingImage.next(this.modelField.missingImage) ;

        // Reset input value so that we can detect file change event even when selecting the same image after paging
        if (this.fileInput) {
          this.fileInput.nativeElement.value = null;
        }
        if (this.isForeGroundText) {
          const text = this.lmFieldServiceAdapter.lmFormatUtilService.formatMessageRule(this.field.foregroundText, this.model, this.specFields);
          this.foreGroundText = this.lmFieldServiceAdapter.lmThemeService.getImageForegroundText(text);
        }

        // Update bg color when next/previous
        if (this.isRandomBackground) {
          this.bgColorClass = this.getBgColorClass();
        }

        if (this.largeImageIs) {
          this.buildLargeImageInfo();
        }
      })
    );

    this.dataFieldAutomationId = `lm-${this.fieldAutomationId}-field`;
  }

  ngAfterViewInit() {
    super.ngAfterViewInit();

    this.contextEntries.push({
      text:   Soho.Locale.translate('Browse'),
      menuId: 'Browse'
    });

    this.contextEntries.push({
      text:   Soho.Locale.translate('Clear'),
      menuId: 'Clear'
    });

    if (this.largeImageIs) {
      this.contextEntries.push({
        text:   Soho.Locale.translate('View'),
        menuId: 'View'
      });
    }
  }

  ngOnDestroy() {
    super.ngOnDestroy();

    this.imageUrl.unsubscribe();
    this.imageUrl = null;
  }

  onBeforeOpen() {
    // disable Clear entry if it's a missing image
    this.contextEntries[ 1 ].disabled = this.missingImage.getValue();
  }

  onImageMenuFired(menuId: string) {
    if (menuId === 'Browse') {
      this.fileInput.nativeElement.click();

      if (this.isForeGroundText) {
        this.foreGroundText = undefined;
      }

    } else if (menuId === 'Clear') {
      // for a 'Clear', the data value sent is an empty string, but the component must display a source image
      this.modelField.value = '';
      this.imageUrl.next(this.missingImageUrl ? this.missingImageUrl : this.lmFieldServiceAdapter.lmFormatUtilService.toImageUrlValue(this.modelField.value, this.lines, this.perspective));
      this.missingImage.next(true);

      // not using soho-trackdirty as the content of the image object can change while the file name can remain the same
      this.model.setDirty(this.bindId, true);

      if (this.isForeGroundText) {
        const msgRule = this.field.foregroundText;
        this.foreGroundText = this.lmFieldServiceAdapter.lmFormatUtilService.formatMessageRule(msgRule, this.model, this.specFields);
      }
    } else if (menuId === 'View') {
      this.openLargeImage(this.largeImageIsInfo);
    }

    return false;
  }

  onFileChanged(event) {
    if (event.srcElement) {
      const files = event.srcElement.files;

      this.modelField.multiPartFile = files[ 0 ];

      this.runFileSelect(event, files[ 0 ]);
    }
  }

  contextMenuClicked = ($event) => {
    this.onContextMenuEvent($event);
  };

  runFileSelect(event, file: File) {
    if (!file.type.match('image.*')) {
      return;
    }
    const reader: FileReader = new FileReader();

    if (ExifReader) {
      fromEvent(reader, 'loadend').subscribe(() => {
        const tags: ExifReader.Tags = ExifReader.load(reader.result as ArrayBuffer);
        const pixDimX = tags['Image Height']?.value;
        const pixDimY = tags['Image Width']?.value;
        const orientation = tags.Orientation?.value;
        this.setBlobValue(file, orientation, this.imgCanvas.nativeElement, pixDimX, pixDimY);
      });
      reader.readAsArrayBuffer(file);
    } else {
      fromEvent(reader, 'loadend').subscribe(() => {
        this.imageUrl.next(reader.result);
        this.modelField.value = reader.result;
        this.missingImage.next(false);
      });
      reader.readAsDataURL(file);
    }

    // not using soho-trackdirty as the content of the image object can change while the file name can remain the same
    this.model.setDirty(this.bindId, true);
  }

  public setBlobValue(file, orientation, canvas, pixDimX, pixDimY) {
    const isLandscape = (orientation === 1 || orientation === 2 || orientation === 3 || orientation === 4 || orientation === undefined);

    const reader: FileReader = new FileReader();
    reader.onload = (e: Event) => {
      const img = new Image();
      img.onload = () => {
        const ctx: CanvasRenderingContext2D = canvas.getContext('2d');

        const MAX_WIDTH = img.width; // isLandscape ? 800 : 600;
        const MAX_HEIGHT = img.height; // isLandscape ? 600 : 800;

        let orientationOption: string;
        if ((pixDimX > pixDimY) && orientation === 6) {
          // when orientation is recognized as portrait but dimensions are landscape. swap dimensions
          const tempHeight = img.height;
          img.height = img.width;
          img.width = tempHeight;

          orientationOption = 'portrait';
        } else if (orientation === 0 && (pixDimX > pixDimY)) { // orientation 0 and landscape
          orientationOption = 'landscape';
        } else if (orientation === 0 && (pixDimX < pixDimY)) { // orientation 0 and portrait
          orientationOption = 'portrait';
        }

        let width = img.width;
        let height = img.height;

        if (width > height) {
          if (width > MAX_WIDTH) {
            height = MAX_HEIGHT;
            width = MAX_WIDTH;
          }
        } else {
          if (height > MAX_HEIGHT) {
            width = MAX_WIDTH;
            height = MAX_HEIGHT;
          }
        }

        canvas.width = isLandscape ? width : height;
        canvas.height = isLandscape ? height : width;

        this.lmFileUploadService.rotateOrFlip(ctx, orientation, orientationOption, width, height);
        let contextLeft = 0;
        let contextTop = 0;

        if (orientation !== 1 && orientation !== undefined) {
          contextLeft = -(width / 2);
          contextTop = -(height / 2);
        }

        ctx.clearRect(0, 0, canvas.width, canvas.height);
        ctx.drawImage(img, contextLeft, contextTop, width, height);

        const dataUrl = canvas.toDataURL(file.type);
        this.imageUrl.next(dataUrl);
        this.modelField.value = dataUrl;
        const blob = this.lmFileUploadService.dataURLToBlob(dataUrl);
        this.modelField.multiPartFile = new Blob([ blob ], { type : file.type });
        this.missingImage.next(false);
      };
      // @ts-ignore
      img.src = e.target.result;
    };
    reader.readAsDataURL(file);
  }

  onClickEvent(event) {
    if (this.field?.linkNavigation) {
      this.fieldEvent.emit(new LMTextEvent(event, this.field));
    }
  }

  trackByMenuId(menuEntry: ImageMenuEntry) {
    return menuEntry.menuId;
  }

  private allowUpdate(): boolean {
    if (this?.field?.type === 'url') {
      return false;
    }

    return this.model.hasUpdateAction || this.model.isCreateAction;
  }

  private buildLargeImageInfo() {
    if (this.largeImageIs
      && (this.model.getField(this.layoutObject.largeImage.fieldId)?.blobUrl)
    ) {
      this.largeImageIsInfo.imgUrl = this.model.getField(this.layoutObject.largeImage.fieldId).blobUrl;
    }

    if (this.largeImageIs?.title) {
      const fieldId = this.layoutObject.largeImage.title.fieldId;
      const title = this.model.getField(fieldId)?.value;
      this.largeImageIsInfo.title = title !== undefined && title !== null ? title : '';
    }

    if (this.largeImageIs?.download) {
      this.largeImageIsInfo.download = {};

      const fieldId = this.layoutObject.largeImage.download.fieldId;
      const field = this.model.getField(fieldId);
      const formRequestUrl = this?.model?.formUrlInfo?.formRequestUrl;

      // Set formRequestUrl & fieldId info for lazy load
      // This could potentially happen for a derived field
      // If field is not null, then just get the value from the model
      if (field === null && formRequestUrl) {
        const compiledFormRequestUrl = urltemplate.parse(formRequestUrl);
        const requestUrl = compiledFormRequestUrl.expand({ resourceId: this.model.formResourceId });

        this.largeImageIsInfo.download.formRequestUrl = requestUrl;
        this.largeImageIsInfo.download.fieldId = fieldId;
      } else if (field) {
        this.largeImageIsInfo.download.downloadUrl = field.blobUrl;
      }
    }
  }

  public openLargeImage(largeImage: LMLargeImageIsInfo) {
    this.lmLargeImageIsDialogService.openLargeImageDialog(largeImage);
  }

  private getBgColorClass(): string {
    return `image-initials ${this.lmFieldServiceAdapter.lmThemeService.getUniqueBackgroundColorClass(this.foreGroundText)}`;
  }

  private checkLabelVisibility(): void {
    const label = this.layoutObject?.label as LMLabelLayout;
    
    // Only show the label for image field when it has an explicit label
    if (label.isExplicitLabel) {
      const imgLabel = this.lmFieldServiceAdapter.lmFormatUtilService.formatMessageRule(this.labelResourceValue, this.model, this.specFields);

      if (label && imgLabel?.length > 0) {
        this.displayLabel = true;
        this.lmFieldServiceAdapter.changeDetectorRef.markForCheck();
      }
    }
  }
}
